import { JSX, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { GetServerSideProps } from 'next';
import NotFound from 'src/NotFound';
import sitecoreRedirect from 'lib/sc-redirect';
import Layout from 'src/Layout';
import {
  SitecoreContext,
  ComponentPropsContext,
  RouteData,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { componentBuilder } from 'temp/componentBuilder';
import { ExtendedStyleguideSitecoreContextValue } from 'src/Layout';
import { rawRequest, gql } from 'graphql-request';
import { getDeliveryUrl, getDeliveryKey } from 'lib/util';
import Head from 'next/head';
import {
  LocationMetaFields,
  MetaProvider,
} from 'components/types/LocationTypes';
import { TrackingProvider } from 'lib/tracking';

const SitecorePage = ({
  notFound,
  componentProps,
  layoutData,
  headLinks,
  location,
  locationProviders,
  linksource,
}: SitecorePageProps & {
  location: LocationMetaFields;
  locationProviders: Array<MetaProvider>;
  linksource: string | null;
}): JSX.Element => {
  const router = useRouter();
  const isRedirecting = useRef(false);
  useEffect(() => {
    if (router.isFallback) return;

    // Handle Experience Editor Fast Refresh
    handleEditorFastRefresh();
  }, [notFound, layoutData?.sitecore?.route]);

  if (!layoutData?.sitecore?.route && !isRedirecting.current) {
    return <NotFound />;
  }

  const isEditing = layoutData.sitecore.context.pageEditing;

  const contextValue: ExtendedStyleguideSitecoreContextValue = {
    sitecore: {
      context: {
        ...layoutData.sitecore.context,
        site: {
          name: layoutData.sitecore.context.site?.name ?? '',
        },
      },
      route:
        (layoutData.sitecore
          .route as ExtendedStyleguideSitecoreContextValue['sitecore']['route']) ||
        ({} as RouteData),
    },
    site: {
      name: layoutData.sitecore.context.site?.name ?? '',
    },
    itemPath: layoutData.sitecore.route?.itemId,
    itemId: layoutData?.sitecore.route?.itemId || undefined,
  };

  return (
    <TrackingProvider value={{ linksource }}>
      <>
        <Head>
          <title>
            {`${location?.LocationName?.value} in ${location?.City?.value}`}
          </title>
        <meta
          name="kyruus-id"
          content={
            location?.LocationId?.value
              ? location.LocationId.value.replace(/^LOC0*/, '')
              : ''
          }
        />
        <meta name="location-name" content={location?.LocationName?.value} />
        <meta name="location-city" content={location?.City?.value} />
        <meta
          name="description"
          content={`Located at ${location?.Street1?.value}, ${location?.City?.value}, ${location?.State?.value}.${
            locationProviders
              ? ` We specialize in ${
                  locationProviders[0]?.Specialty?.value
                    ? locationProviders[0]?.Specialty?.value
                        .split('|')
                        .map((item: string) => item.trim())[0]
                    : ''
                }.`
              : ''
          } Schedule an appointment today at ${location.Phone?.value}.`}
        />
        <meta
          property="twitter:image"
          content={
            location?.LocationImage?.value?.value &&
            typeof location.LocationImage.value.value === 'object' &&
            'src' in location.LocationImage.value.value
              ? (location.LocationImage.value.value as { src?: string }).src ||
                ''
              : ''
          }
        />
        <meta
          name="twitter:title"
          content={`${location?.LocationName?.value} in ${location?.City?.value}`}
        />
        <meta
          name="twitter:description"
          content={`Located at ${location?.Street1?.value}, ${location?.City?.value}, ${location?.State?.value}.${
            locationProviders
              ? ` We specialize in ${
                  locationProviders[0]?.Specialty?.value
                    ? locationProviders[0]?.Specialty?.value
                        .split('|')
                        .map((item: string) => item.trim())[0]
                    : ''
                }.`
              : ''
          } Schedule an appointment today at ${location.Phone?.value}.`}
        />
        <meta
          property="og:image"
          content={
            location?.LocationImage?.value?.value &&
            typeof location.LocationImage.value.value === 'object' &&
            'src' in location.LocationImage.value.value
              ? (location.LocationImage.value.value as { src?: string }).src ||
                ''
              : ''
          }
        />
        <meta
          property="og:title"
          content={`${location?.LocationName?.value} in ${location?.City?.value}`}
        />
        <meta
          property="og:description"
          content={`Located at ${location?.Street1?.value}, ${location?.City?.value}, ${location?.State?.value}.${
            locationProviders
              ? ` We specialize in ${
                  locationProviders[0]?.Specialty?.value
                    ? locationProviders[0]?.Specialty?.value
                        .split('|')
                        .map((item: string) => item.trim())[0]
                    : ''
                }.`
              : ''
          } Schedule an appointment today at ${location.Phone?.value}.`}
        />
      </Head>
      <ComponentPropsContext value={componentProps}>
        <SitecoreContext
          componentFactory={componentBuilder.getComponentFactory({
            isEditing,
          })}
          layoutData={layoutData}
        >
          <Layout
            context={contextValue}
            layoutData={layoutData}
            headLinks={headLinks}
            schemaText={location?.SchemaText}
          />
        </SitecoreContext>
      </ComponentPropsContext>
    </>
  </TrackingProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const linksource = context.req.cookies['linksource'] ?? null;
    if (context.params?.path) {
        let sourceUrl = '/locations';
        if (Array.isArray(context.params.path)) {
            sourceUrl += '/' + context.params.path.join('/');
        } else {
            sourceUrl += '/' + context.params.path;
        }

        const redirect = await sitecoreRedirect(sourceUrl);
        if (redirect) {
            return {
                redirect: {
                    destination: redirect.destination,
                    statusCode: redirect.permanent ? 301 : 302,
                },
            };
        }
    }

    let location;
    let locationProviders;
    if (context.params) {
        if (context.params.path) {
            const locationItemName = context.params.path.length
                ? context.params.path[context.params.path.length - 1]
                : context.params.path[1];
            const locationId = locationItemName?.substring(
                locationItemName.lastIndexOf('-') + 1,
                locationItemName.length
            );
            const deliveryKey = getDeliveryKey();
            const endpoint = getDeliveryUrl();

            const locationQuery = gql`
        query ($locationId: String!) {
          search(
            where: {
              AND: [
                {
                  name: "_path"
                  value: "{F7971D27-B938-4280-A061-E92A81AE8C99}"
                  operator: CONTAINS
                }
                { name: "LocationID", value: $locationId, operator: CONTAINS }
              ]
            }
            first: 1
          ) {
            results {
              id
              City: field(name: "City") {
                value: value
              }
              LocationName: field(name: "LocationName") {
                value: value
              }
              LocationId: field(name: "LocationId") {
                value: value
              }
              Phone: field(name: "Phone") {
                value: value
              }
              State: field(name: "State") {
                value: value
              }
              Street1: field(name: "Street1") {
                value: value
              }
              Suite: field(name: "Suite") {
                value: value
              }
              Zip: field(name: "Zip") {
                value: value
              }
              LocationImage: field(name: "LocationImage") {
                value: jsonValue
              }
              SchemaText: field(name: "SchemaText") {
                value
              }
            }
          }
        }
      `;
            const locationProvidersQuery = gql`
        query ($locationItemId: String!) {
          search(
            where: {
              AND: [
                {
                  name: "_path"
                  value: "{6A68FA22-BE0C-46BB-912C-22F1F83E28DC}"
                  operator: CONTAINS
                }
                {
                  name: "Locations"
                  value: $locationItemId
                  operator: CONTAINS
                }
              ]
            }
            first: 1
          ) {
            results {
              Specialty: field(name: "Specialty") {
                value
              }
            }
          }
        }
      `;
            const locationVariables = { locationId: locationId };
            const headers = {
                sc_apikey: deliveryKey,
            };

            const getLocation = async () => {
                try {
                    const { data } = await rawRequest(
                        endpoint,
                        locationQuery,
                        locationVariables,
                        headers
                    );
                    return data.search.results[0] as LocationMetaFields;
                } catch (error) {
                    console.error(error);
                    return null;
                }
            };
            location = await getLocation();

            const locationItemId = `{${location?.id?.substring(0, 8)}-${location?.id?.substring(8, 12)}-${location?.id?.substring(12, 16)}-${location?.id?.substring(16, 20)}-${location?.id?.substring(20)}}`;
            const locationProvidersVariables = { locationItemId: locationItemId };

            const getLocationProviders = async () => {
                try {
                    const { data } = await rawRequest(
                        endpoint,
                        locationProvidersQuery,
                        locationProvidersVariables,
                        headers
                    );
                    if (data?.search?.results?.length > 0) {
                        const locProviders: Array<MetaProvider> = data.search.results;
                        //Filters out providers without specialties in specialty text field
                        return locProviders.filter(
                            provider =>
                                provider.Specialty?.value?.length &&
                                provider.Specialty?.value?.length > 0
                        );
                    } else {
                        return null;
                    }
                } catch (error) {
                    console.error(error);
                    return null;
                }
            };

            locationProviders = await getLocationProviders();
        }
        context.params.requestPath = context.params.path;
        context.params.path = [`locations/,-w-,`];
    }
    const props = await sitecorePagePropsFactory.create(context);

    return {
        props: {
            ...props,
            location: location,
            locationProviders: locationProviders,
            linksource
        },
        notFound: props.notFound,
    };
};

export default SitecorePage;
